import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { useHistory } from "react-router-dom";
import { Button } from "@mui/material";
import Filters from "./filters/Filters";
import Analysis from "./Analysis";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  clearFilters,
  setAnalysisType,
  setHazard,
} from "../../actions/filters";
import { Alert, AlertTitle } from "@mui/lab";
import { parseSearch } from "../../actions/helpers";
import CircularProgress from "@mui/material/CircularProgress";

const PREFIX = 'Analyses';

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  clear: `${PREFIX}-clear`,
  empty: `${PREFIX}-empty`,
  loadingContainer: `${PREFIX}-loadingContainer`
};

const Root = styled('div')((
  {
    theme
  }
) => ({

  [`& .${classes.header}`]: {
    textTransform: "uppercase",
  },

  [`& .${classes.clear}`]: {
	color: "rgba(0, 0, 0, 0.87)",
    background: "#fff",
    margin: "-0.4em 0 0 2em",
    fontSize: "0.7rem",
    border: "1px solid #d3d3d3 !important",
    borderRadius: "50px",
    padding: "0.1em 0.5em",
    "&:hover": {
      background: "#f9f9f9",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    },
  },

  [`& .${classes.empty}`]: {
    margin: "1em 0",
  },

  [`& .${classes.loadingContainer}`]: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    margin: "2rem 0",
  }
}));

const Analyses = ({ analyses, filters, clearFilters }) => {

  const history = useHistory();
  const [hasAnalyses, setHasAnalyses] = useState(undefined);
  const [renderAnalyses, setRenderAnalyses] = useState([]);

  const resetHistory = () => {
    const searchParams = parseSearch();
    searchParams.hazard && delete searchParams.hazard;
    searchParams.analysisType && delete searchParams.analysisType;
    searchParams.sort && delete searchParams.sort;
    const search = new URLSearchParams(searchParams).toString();
    history.push({
      pathname: "/details",
      search: search,
    });
  };

  const clear = () => {
    clearFilters();
    resetHistory();
  };

  const Empty = () => {
    return (
      <Alert severity="error" className={classes.empty}>
        <AlertTitle>No Analyses Available</AlertTitle>
        Try clearing the filters to see available analyses
      </Alert>
    );
  };

  useEffect(() => {
    if (typeof analyses !== "undefined" && analyses.length > 0) {
      // filter hazard
      let _analyses =
        Object.keys(filters.hazard).filter((key) => filters.hazard[key])
          .length > 0
          ? analyses.filter((analysis) =>
              analysis.hazard.some((item) => filters.hazard[item])
            )
          : [...analyses];

      // filter analysisType
      _analyses =
        Object.keys(filters.analysisType).filter(
          (key) => filters.analysisType[key]
        ).length > 0
          ? _analyses.filter(
              (analysis) => filters.analysisType[analysis.analysisType]
            )
          : [..._analyses];

      // filter economicLossMin
      if (filters.economicLossMin !== null) {
        _analyses = _analyses.filter(
          (analysis) => analysis.totalLosses >= filters.economicLossMin
        );
      }
      // filter economicLossMax
      if (filters.economicLossMax !== null) {
        _analyses = _analyses.filter(
          (analysis) => analysis.totalLosses <= filters.economicLossMax
        );
      }

      setRenderAnalyses(_analyses);

      _analyses.length > 0 ? setHasAnalyses(true) : setHasAnalyses(false);
    } else {
      setHasAnalyses(undefined);
    }
  }, [analyses, analyses.length, filters]);

  return (
    <Root className={classes.root}
		sx={{
			width: "100%",
		    position: "relative",
	}}>
	      <span className={classes.header}>Analyses</span>
	      <Button
	        className={classes.clear}
	        onClick={clear}
	        aria-label="clear analysis filters"
	      >
	        Clear Filters
	      </Button>
	      <Filters />
	      {hasAnalyses ? (
	        renderAnalyses.map((analysis) =>
	          !analysis.pinned ? <Analysis analysis={analysis} /> : null
	        )
	      ) : hasAnalyses === undefined ? (
	        <div className={classes.loadingContainer}>
	          <CircularProgress />
	        </div>
	      ) : (
	        <Empty />
	      )}
    </Root>
  );
};

Analyses.propTypes = {
  clearFilters: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  analyses: state.details.analyses,
  filters: state.filters,
});

export default connect(mapStateToProps, { clearFilters })(Analyses);
